import React from "react";
import { Link } from "react-router-dom";

export const GATE_BACKGROUND =
  "radial-gradient(circle at 50% 0%, rgba(49, 135, 229, 0.26), transparent 48%), linear-gradient(155deg, #0d2341 0%, #102e52 48%, #071a31 100%)";

const YES_CLASS =
  "w-full sm:w-52 inline-flex items-center justify-center bg-[#3484e7] text-white hover:bg-[#5ca0ee] font-semibold px-8 py-[18px] text-base tracking-wide rounded-full shadow-[0_16px_42px_rgba(24,107,211,0.32)] hover:shadow-[0_20px_48px_rgba(24,107,211,0.42)] transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#102e52]";

/**
 * The research confirmation shared by the /research-eligibility page and the
 * on-page overlay. `yesTo` renders Yes as a link; without it Yes is a button.
 */
export function ResearchConfirmationPanel({ headingAs: Heading = "h1", headingId, yesTo, onYes, onNo }) {
  const YesTag = yesTo ? Link : "button";
  return (
    <>
      {/* ── Centered qualification screen ─────────────────────────────── */}
      <div className="max-w-4xl mx-auto w-full text-center py-4 sm:py-6">
        <div
          data-testid="gate-made-in-usa"
          className="inline-flex items-center gap-2 rounded-full border border-[#8bb9e8]/50 bg-white/10 px-4 py-1.5 mb-6 text-[11px] sm:text-[12px] font-semibold uppercase tracking-[0.18em] text-[#dceaff]"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#3484e7]" aria-hidden />
          Peptides Made in the USA
        </div>

        <Heading
          id={headingId}
          className="font-display !text-white font-semibold tracking-[-0.045em] text-4xl sm:text-5xl md:text-[4.25rem] leading-[1.04] drop-shadow-[0_12px_32px_rgba(0,0,0,0.16)]"
          style={{ color: "#fff" }}
          data-testid="gate-headline"
        >
          America&rsquo;s #1 Peptide Source
        </Heading>

        <p
          className="mt-7 sm:mt-8 font-display !text-[#d8e7f8] text-xl sm:text-2xl leading-snug"
          style={{ color: "#d8e7f8" }}
          data-testid="gate-question"
        >
          Are you looking for peptides?
        </p>

        <div
          className="mt-9 sm:mt-11 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-5"
          data-testid="gate-answer-buttons"
        >
          <YesTag
            {...(yesTo ? { to: yesTo } : { type: "button" })}
            onClick={onYes}
            className={YES_CLASS}
            data-testid="gate-yes-button"
            data-analytics-event="eligibility_yes"
            aria-label="Yes — Shop peptides"
          >
            YES &mdash; SHOP PEPTIDES
          </YesTag>

          <Link
            to="/about"
            onClick={onNo}
            className="w-full sm:w-52 inline-flex items-center justify-center border border-[#8bb9e8]/70 text-white hover:border-white hover:bg-white/10 font-semibold px-8 py-[18px] text-base tracking-wide rounded-full transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#102e52]"
            data-testid="gate-no-button"
            data-analytics-event="eligibility_no"
            aria-label="No — Learn more"
          >
            NO &mdash; LEARN MORE
          </Link>
        </div>
      </div>

      {/* ── Fine-print Research-Use disclosure (bottom-anchored) ──────── */}
      <div className="max-w-3xl mx-auto w-full mt-10 sm:mt-14">
        <p
          className="text-center text-[12px] sm:text-[13px] leading-relaxed text-[#b6c8df]"
          data-testid="gate-fine-print"
        >
          For research use only. Not for human consumption or veterinary use. Not intended to
          diagnose, treat, cure, or prevent any disease. Reference standards ship with lot-specific
          documentation for identity and purity. Use outside a qualified research setting is
          prohibited by our{" "}
          <Link
            to="/research-use-policy"
            className="underline underline-offset-4 text-[#dceaff] hover:text-white"
            data-testid="gate-research-use-policy-link"
          >
            Research-Use Policy
          </Link>
          .
        </p>

        <div
          className="mt-6 pt-4 border-t border-[#7296bc]/30 flex flex-wrap gap-x-5 gap-y-1 justify-center text-[11px] text-[#a7bed8]"
          data-testid="gate-footer-links"
        >
          <Link to="/research-use-policy" className="hover:text-white">Research-Use Policy</Link>
          <Link to="/regulatory-disclaimer" className="hover:text-white">Regulatory Disclaimer</Link>
          <Link to="/quality" className="hover:text-white">Quality &amp; Testing</Link>
          <Link to="/privacy" className="hover:text-white">Privacy</Link>
          <Link to="/contact" className="hover:text-white">Contact</Link>
        </div>
      </div>
    </>
  );
}
